// NATIVE IMPORTS
require("./ApplicationDetails.scss");
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router";

// SERVICES
import { setHelpRequests } from "src/store/features/applications/applicationsSlice";
import {
  getApplicationById,
  getConfigurations,
  getIsDocumentScreeningFeatureEnabled,
  getIsFurtherReviewFeatureEnabled,
  getIsIncSecDepositFeatureEnabled,
  getIsSecDepositFeatureEnabled,
} from "../../services/AdminDashService";
import { getHelpRequestsBulk } from "../../services/HelpRequestService";
import LocalStorageService from "../../services/LocalStorageService";

// COMPONENTS
import HelpRequestModal from "../AdminDashboard/HelpRequestModal";
import Applicants from "../ApplicationDetails/Applicants";
import CompleteApplicationModal from "../ApplicationDetails/CompleteApplicationModal";
import LeaseProcess from "../ApplicationDetails/LeaseProcess";
import LeftPanel from "../ApplicationDetails/LeftPanel";
import RightPanel from "../ApplicationDetails/RightPanel";
import Screening from "../ApplicationDetails/Screening";
import Footer from "../Shared/Footer";
import Header from "../Shared/Header";
import InformationIcon from "../Shared/icons/InformationIcon";
import RedFlagIcon from "../Shared/icons/RedFlagIcon";

// UTILITIES
import { Alert } from "react-bootstrap";
import { getConfigs } from "src/configs/Config";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { resetNotification } from "src/store/features/applications/applicationsSlice";
import { ApplicationStatusId, ReduxApplication } from "src/types/Application";
import { capitalize, formatUnit, getRiskLevel } from "../../utils/Utils";
import { hasUnresolvedHighRisk } from "../../utils/Application";
import DocumentScreening from "../ApplicationDetails/DocumentsScreening";
import LastFetchedDate from "../Shared/LastFetchedDate";
import WarningModal from "../Shared/WarningModal";
import type { Configuration } from "src/types/Redux";
import { Payment, PaymentStatus, PaymentType } from "src/types/Payment";
import RiskAssessment from "../ApplicationDetails/RiskAssessment";
import PauseIndicatorBanner from "../ApplicationDetails/PauseIndicatorBanner";
import { Application } from "src/types/Application";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.tz.setDefault("America/New_York");

export interface IApplicant {
  applicantTypeId: number;
}

const ApplicationDetails = () => {
  const { refetchDataIntervalTime } = getConfigs();

  const { id } = useParams();

  const applicationIdRegex = /^[0-9]+$/;

  const isValidUrlParam =
    id && typeof id === "string" && applicationIdRegex.test(id) === true;

  const helpRequests = useAppSelector(
    (state) => state.applications.helpRequests
  );
  const applicationFromState = useAppSelector(
    (state) => state.applications.application
  );
  const notification = useAppSelector(
    (state) => state.applications.notification
  );

  const payments: Payment[] | null = useAppSelector(
    (state) => state.applications.payments ?? null
  );
  const { secDepositPaymentStatus } = useMemo(() => {
    if (payments === null) {
      return {
        secDepositPaymentStatus: null,
        secDepositPaymentType: null,
      };
    }

    const secDepPayment = payments?.find(
      (payment) =>
        [
          PaymentType.SecurityDeposit,
          PaymentType.IncreasedSecurityDeposit,
        ].includes(payment.paymentType as PaymentType) &&
        payment.paymentStatus === PaymentStatus.Accepted
    );

    return {
      secDepositPaymentStatus: secDepPayment?.paymentStatus ?? undefined,
      secDepositPaymentType:
        secDepPayment?.paymentType ?? PaymentType.SecurityDeposit,
    };
  }, [payments]);

  const configuration: Configuration =
    useAppSelector((state) => state.applications.configuration) ??
    ({} as Configuration);

  let [email, setEmail] = useState();
  const [isChargesLoaded, setIsChargesLoaded] = useState<boolean>(false);
  const [applicationRequests, setApplicationRequests]: any = useState([]);
  const [application, setApplication] = useState<ReduxApplication | undefined>(
    undefined
  );
  const [showRequest, setShowRequest] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDocumentValidation, setShowDocumentValidation] = useState<
    boolean | null
  >(null);
  const [isRentStabilizationCommunity, setIsRentStabilizationCommunity] =
    useState<boolean>(false);
  const [isFurtherReviewEnabled, setIsFurtherReviewEnabled] =
    useState<boolean>(false);
  const refetchTimerId = useRef<undefined | NodeJS.Timer>(undefined);
  const {
    application: applicationFromLocal,
    helpRequests: helpRequestsFromLocal,
  } = LocalStorageService.getApplication(id);
  const lastFetchedDate =
    application?.lastFetchedDate ?? applicationFromLocal?.lastFetchedDate ?? "";

  const dispatch = useAppDispatch();

  const transferHelpRequestToState = (helpRequestsFromLocal) => {
    if (!helpRequestsFromLocal || !helpRequestsFromLocal.length) {
      dispatch(
        setHelpRequests({
          helpRequests: [],
        })
      );
      return;
    }

    dispatch(
      setHelpRequests({
        helpRequests: helpRequestsFromLocal[0].helpRequestList,
      })
    );
  };

  const getHelpRequest = () => {
    return helpRequests.filter((r) => r.applicationId + "" === id);
  };

  const updateLocalStorage = () => {
    if (isValidUrlParam) {
      const helpRequests = getHelpRequest();
      LocalStorageService.setApplicationInLocal(
        applicationFromLocal,
        helpRequests
      );
    }
  };

  useEffect(() => {
    if (Object.keys(applicationFromState).length != 0) {
      setApplication(applicationFromState as ReduxApplication);
      return;
    }
  }, [JSON.stringify(applicationFromState)]);

  // Initialize application data
  useEffect(() => {
    dispatch(getConfigurations());
    if (isValidUrlParam) {
      dispatch(getApplicationById(id));
      dispatch(getHelpRequestsBulk([id]));
      transferHelpRequestToState(helpRequestsFromLocal);
      if (!!applicationFromLocal && !application) {
        setApplication(applicationFromLocal);
      }
    }
  }, []);

  useEffect(() => {
    if (application?.monthlyCharges && application.monthlyCharges.length > 0) {
      setIsChargesLoaded(true);
    }
  }, [application]);

  const refreshApplicationData = useCallback(() => {
    if (id && isValidUrlParam) {
      dispatch(getApplicationById(id));
      dispatch(getHelpRequestsBulk([id]));
    }
  }, [id]);

  // Refetch data when window is on focus
  useEffect(() => {
    const inactiveStatuses = [
      ApplicationStatusId.Rejected,
      ApplicationStatusId.Completed,
      ApplicationStatusId.Removed,
      ApplicationStatusId.Cancelled,
      ApplicationStatusId.Closed,
      ApplicationStatusId.ApplicationComplete,
    ];
    if (
      application?.applicationStatusId == undefined ||
      inactiveStatuses.includes(application?.applicationStatusId)
    ) {
      return;
    }

    const initRefetchTimer = () => {
      if (refetchTimerId.current) clearInterval(refetchTimerId.current);
      refetchTimerId.current = setInterval(
        refreshApplicationData,
        refetchDataIntervalTime
      );
    };

    const stopRefetchTimer = () => {
      if (refetchTimerId.current) {
        clearInterval(refetchTimerId.current);
        refetchTimerId.current = undefined;
      }
    };

    const visibilitychange = () => {
      if (document.hidden) {
        stopRefetchTimer();
      } else {
        initRefetchTimer();
      }
    };

    initRefetchTimer();
    document.addEventListener("visibilitychange", visibilitychange);
    window.addEventListener("focus", initRefetchTimer);
    window.addEventListener("blur", stopRefetchTimer);

    return () => {
      stopRefetchTimer();
      document.removeEventListener("visibilitychange", visibilitychange);
      window.removeEventListener("focus", initRefetchTimer);
      window.removeEventListener("blur", stopRefetchTimer);
    };
  }, [refreshApplicationData, application?.applicationStatusId]);

  useEffect(() => {
    window.localStorage.setItem("TAB_OPENED", "");
    window.localStorage.removeItem("TAB_OPENED");

    const storageEvent = (event) => {
      if (!event.newValue) return;
      if (event.key === "SENDING_CREDENTIALS" && event.newValue) {
        const data = JSON.parse(event.newValue);
        window.sessionStorage.setItem("email", data.email);
        window.sessionStorage.setItem("token", data.token);
        setEmail(data.email);
      }
    };
    window.removeEventListener("storage", storageEvent);
    window.addEventListener("storage", storageEvent);
  }, [application]);

  useEffect(() => {
    if (isValidUrlParam) {
      loadHelpRequests();
      updateLocalStorage();
    }
  }, [helpRequests]);

  const loadHelpRequests = () => {
    if (isValidUrlParam) {
      const filterData = getHelpRequest();
      setApplicationRequests(
        filterData[0]?.helpRequestList
          .filter((f) => {
            return f.helpStatus.toLowerCase() === "new";
          })
          .sort((a, b) => {
            const c =
              (a.modifiedDate && new Date(a.modifiedDate)) ||
              (a.createdDate && new Date(a.createdDate));
            const d =
              (b.modifiedDate && new Date(b.modifiedDate)) ||
              (b.createdDate && new Date(b.createdDate));
            return d - c;
          })
      );
    }
  };

  useEffect(() => {}, [applicationRequests]);

  useEffect(() => {
    if (configuration && application && application.communityId) {
      const isDocumentScreeningFeatureEnabled = Boolean(
        application?.isDocumentScreeningRequired ||
          getIsDocumentScreeningFeatureEnabled(
            configuration?.features,
            application?.communityId,
            application?.configuration
          )
      );
      setShowDocumentValidation(isDocumentScreeningFeatureEnabled);
      const isFurtherReviewEnabled = getIsFurtherReviewFeatureEnabled(
        configuration?.features,
        application?.communityId
      );
      setIsFurtherReviewEnabled(isFurtherReviewEnabled);
      // Rent Stabilization communities enabled
      const docusignFeatures = configuration.features?.docusign as {
        [key: string]: string[] | null;
      };
      const moveInDate = dayjs
        .tz(application?.moveInDate, "America/New_York")
        // Add a 1-minute offset so that lease begin dates at exactly midnight
        // are treated as occurring after `hide421AAfterLeaseBeginDate`, which is
        // normalized with `.startOf("day")`. This avoids edge cases where a
        // midnight move-in would otherwise not be considered "after" that date.
        .add(1, "minute");
      const hide421AAfterDate =
        application?.communityId &&
        docusignFeatures?.hide421AAfterLeaseBeginDate?.[application.communityId]
          ? dayjs(
              docusignFeatures?.hide421AAfterLeaseBeginDate?.[
                application.communityId
              ]
            ).startOf("day")
          : null;

      const is421AHidden = moveInDate.isAfter(hide421AAfterDate);
      const rentStabilizationCommunities = (
        docusignFeatures?.addendums as unknown as {
          [key: string]: string[] | null;
        }
      )?.rentStabilization as string[];
      const rentStabilizationIfAffordable = (
        docusignFeatures?.addendums as unknown as {
          rentStabilizationIfAffordable: string[];
        }
      )?.rentStabilizationIfAffordable as string[];

      const isRentStabilizationCommunity = Boolean(
        rentStabilizationCommunities?.length === 0 ||
          rentStabilizationCommunities?.includes(application?.communityId)
      );
      const isRentStabilizationIfAffordableCommunity = Boolean(
        rentStabilizationIfAffordable?.length === 0 ||
          (rentStabilizationIfAffordable?.includes(application?.communityId) &&
            application?.isAffordable)
      );

      setIsRentStabilizationCommunity(
        (isRentStabilizationCommunity ||
          isRentStabilizationIfAffordableCommunity) &&
          !is421AHidden
      );
    }
  }, [configuration, application]);

  const showRequestModal = (request) => {
    setSelectedRequest(request);
    setShowRequest(true);
  };

  const closeNotification = () => {
    dispatch(resetNotification());
  };

  const getPrimaryApplicant = () => {
    return (
      application && application.applicants.find((x) => x.applicantTypeId == 1)
    );
  };

  const closeReadyToFinishNotification = (e) => {
    if (e) {
      e.stopPropagation();
      setShowConfirmationModal(false);
    }
  };

  const isIncSecDepositEnabled =
    !!application?.communityId &&
    !!configuration?.features &&
    getIsIncSecDepositFeatureEnabled(
      configuration.features,
      application.communityId
    );

  const isSecDepositEnabled =
    !!application?.communityId &&
    !!configuration?.features &&
    getIsSecDepositFeatureEnabled(
      configuration.features,
      application.communityId
    );
  const shouldRenderSecurityDepositSection =
    isIncSecDepositEnabled || isSecDepositEnabled;
  return !isValidUrlParam ? (
    <div id="avb-application-details">
      <h1>Invalid Url Parameter</h1>
    </div>
  ) : (
    <>
      {
        <div id="avb-application-details">
          <div className="admin-header">
            <Header email={email} />
          </div>
          {application &&
            application.unitId &&
            showDocumentValidation != null && (
              <div className="admin-body container-fluid">
                <div className="heading admin-body_title d-flex justify-content-between flex-wrap align-items-center">
                  <div>
                    Application Details: Unit {formatUnit(application.unitId)}{" "}
                    {capitalize(application.communityName)}
                  </div>
                  <div>
                    <LastFetchedDate
                      date={lastFetchedDate}
                      onRefresh={refreshApplicationData}
                    />
                  </div>
                </div>
                {notification && notification.type === "INFO" && (
                  <Alert
                    variant="success"
                    onClose={closeNotification}
                    dismissible
                  >
                    {notification.message}
                  </Alert>
                )}
                {hasUnresolvedHighRisk(application, configuration) && (
                  <PauseIndicatorBanner application={application} />
                )}
                {applicationRequests && applicationRequests.length > 0 && (
                  <div className="help-requests">
                    <ul>
                      {applicationRequests?.map((r) => {
                        return (
                          <li key={r.id}>
                            <RedFlagIcon className="request-flag-icon" />
                            <div className="request-topic">
                              <b>HELP REQUEST</b>
                              &nbsp;&nbsp;&nbsp;&nbsp;
                              <span className="topic">
                                {r.helpRequestTopic}
                              </span>
                            </div>

                            <button
                              className="resolve"
                              onClick={() => showRequestModal(r)}
                            >
                              Resolve
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {(application?.readyToFinishCount ?? 0) > 0 && (
                  <div className="ready-to-finish-notification">
                    <InformationIcon className="information-icon" />
                    <div className="msg">
                      <b>THIS APPLICATION IS COMPLETE AND READY TO FINISH</b>
                    </div>
                    <button
                      className="finish-application"
                      onClick={() => setShowConfirmationModal(true)}
                    >
                      Finish Application
                    </button>
                  </div>
                )}
                <div className="row sections">
                  <LeftPanel
                    shouldRenderSecurityDepositSection={
                      shouldRenderSecurityDepositSection
                    }
                    application={application}
                    secDepositPaymentStatus={secDepositPaymentStatus}
                    refreshApplicationData={() => {
                      refreshApplicationData();
                    }}
                  />
                  <RightPanel>
                    <Applicants
                      application={application}
                      configurationData={configuration}
                    />
                    <Screening application={application} />
                    {showDocumentValidation && (
                      <DocumentScreening application={application} />
                    )}
                    <LeaseProcess
                      application={application}
                      isRentStabilizationCommunity={
                        isRentStabilizationCommunity
                      }
                      isChargesLoaded={isChargesLoaded}
                      configurationData={configuration}
                    />
                    {isFurtherReviewEnabled && (
                      <RiskAssessment
                        configurationData={configuration}
                        application={application}
                      />
                    )}
                  </RightPanel>
                </div>
                <Footer />
                <WarningModal />
                <HelpRequestModal
                  show={showRequest}
                  onCancel={() => setShowRequest(false)}
                  request={selectedRequest}
                  applicant={application}
                />
                <CompleteApplicationModal
                  selectedApplicant={getPrimaryApplicant()}
                  selectedApplication={application}
                  onCancel={closeReadyToFinishNotification}
                  showModal={showConfirmationModal}
                  setShowModal={setShowConfirmationModal}
                />
              </div>
            )}
        </div>
      }
    </>
  );
};

export default ApplicationDetails;
